import { useEffect, useRef, useState } from 'react';
import { uploadMedia, type UploadedMedia } from '../api/client';
import { ru } from '../i18n/ru';
import type { CommentItem } from '../types';

interface Props {
  onSubmit: (text: string, media?: UploadedMedia) => Promise<void>;
  disabled?: boolean;
  replyTo: CommentItem | null;
  onCancelReply: () => void;
  editing: CommentItem | null;
  onCancelEdit: () => void;
  isAdmin: boolean;
  channelName: string;
  asChannel: boolean;
  onSetAsChannel: (v: boolean) => void;
}

function mediaOf(comment: CommentItem): UploadedMedia | null {
  if (!comment.media_file_id || !comment.media_type) return null;
  return { media_file_id: comment.media_file_id, media_type: comment.media_type };
}

export function CommentForm({
  onSubmit,
  disabled,
  replyTo,
  onCancelReply,
  editing,
  onCancelEdit,
  isAdmin,
  channelName,
  asChannel,
  onSetAsChannel,
}: Props) {
  const [text, setText] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [media, setMedia] = useState<UploadedMedia | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Вход в режим правки — форма заполняется текущим содержимым комментария,
  // выход — очищается. Пересобираем только при смене комментария, чтобы не
  // затирать то, что пользователь уже успел напечатать.
  useEffect(() => {
    setError(null);
    setText(editing ? editing.text ?? '' : '');
    setMedia(editing ? mediaOf(editing) : null);
  }, [editing?.id]);

  const canSend = (text.trim().length > 0 || media !== null) && !submitting && !disabled;

  async function handle(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSend) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(text.trim(), media ?? undefined);
      setText('');
      setMedia(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : ru.submitError);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      setMedia(await uploadMedia(file));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : ru.attachError);
    } finally {
      setUploading(false);
    }
  }

  return (
    <form className="comment-form" onSubmit={handle}>
      {editing && (
        <div className="reply-bar">
          <div className="reply-bar-body">
            <span className="reply-bar-author">{ru.editButton}</span>
            <span className="reply-bar-text">{editing.text || '📎'}</span>
          </div>
          <button
            type="button"
            className="reply-bar-x"
            onClick={onCancelEdit}
            aria-label={ru.cancelEdit}
          >
            ✕
          </button>
        </div>
      )}

      {replyTo && !editing && (
        <div className="reply-bar">
          <div className="reply-bar-body">
            <span className="reply-bar-author">{replyTo.author_display_name}</span>
            <span className="reply-bar-text">{replyTo.text || '📎'}</span>
          </div>
          <button
            type="button"
            className="reply-bar-x"
            onClick={onCancelReply}
            aria-label={ru.cancelReply}
          >
            ✕
          </button>
        </div>
      )}

      {media && (
        <div className="media-chip">
          <span>📎 {media.media_type === 'video' ? 'видео' : 'фото'}</span>
          <button type="button" onClick={() => setMedia(null)} aria-label={ru.removeMedia}>
            ✕
          </button>
        </div>
      )}

      {error && <div className="form-error">{error}</div>}

      {pickerOpen && (
        <div className="sheet-overlay" onClick={() => setPickerOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-title">{ru.postAs}</div>
            <button
              type="button"
              className="sheet-row"
              onClick={() => {
                onSetAsChannel(false);
                setPickerOpen(false);
              }}
            >
              <span className="sheet-ava you">{ru.you[0]}</span>
              <span className="sheet-name">{ru.you}</span>
              {!asChannel && <span className="sheet-check">✓</span>}
            </button>
            <button
              type="button"
              className="sheet-row"
              onClick={() => {
                onSetAsChannel(true);
                setPickerOpen(false);
              }}
            >
              <span className="sheet-ava chan">{(channelName[0] || 'К').toUpperCase()}</span>
              <span className="sheet-name">{channelName}</span>
              {asChannel && <span className="sheet-check">✓</span>}
            </button>
          </div>
        </div>
      )}

      <div className="form-row">
        {isAdmin && (
          <button
            type="button"
            className="identity-btn"
            aria-label={ru.postAs}
            onClick={() => setPickerOpen(true)}
          >
            <span className={'identity-ava' + (asChannel ? ' chan' : '')}>
              {asChannel ? (channelName[0] || 'К').toUpperCase() : ru.you[0]}
            </span>
          </button>
        )}
        <button
          type="button"
          className="attach-btn"
          aria-label={ru.attach}
          disabled={submitting || disabled || uploading}
          onClick={() => fileRef.current?.click()}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M8 12.5l5.5-5.5a3 3 0 014.2 4.2l-7 7a5 5 0 01-7-7l7.3-7.3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/mp4"
          hidden
          onChange={handleFile}
        />
        <textarea
          className="comment-input"
          placeholder={ru.placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={submitting || disabled}
          rows={1}
          maxLength={4000}
        />
        <button
          type="submit"
          className="send-btn"
          aria-label={editing ? ru.editSave : ru.submit}
          disabled={!canSend}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d={editing ? 'M5 13l4.5 4.5L19 7.5' : 'M12 20V5M6 11l6-6 6 6'}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </form>
  );
}
